import { DeleteOutlined, DragOutlined, PlusOutlined } from '@ant-design/icons';
import * as api from '@apis/feeds';
import FileUploadButton from '@components/common/FileUploadButton';
import { CustomerBenefitEventsPreview } from '@components/events/CustomerBenefitEventsPreview';
import { DeeplinkActionEdit } from '@components/feed/DeepLinkActionEdit';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CustomerBenefitEventItem, Feed, FeedData } from '@models/feed';
import { useMutation, useQuery } from '@tanstack/react-query';
import { removeTempProperties } from '@utils/dataTransform';
import { cdnImageUrl, resizeImage } from '@utils/image';
import { extractFileName } from '@utils/string';
import { Button, Card, ColorPicker, InputNumber, message, Space } from 'antd';
import { RcFile } from 'antd/es/upload/interface';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  fontSize: '14px',
  transition: 'all 0.3s',
  width: '100%',
  resize: 'none' as const,
  minHeight: '60px',
  maxHeight: '60px',
  lineHeight: '1.5',
};

interface SortableVerticalBannerItemProps {
  onDeleteItem?: (event: any) => void;
  onChange?: (item: CustomerBenefitEventItem) => void;
  onChangeImage?: (files: FileList) => void;
  id: any;
  item: CustomerBenefitEventItem;
}

const SortableVerticalBannerItem: React.FC<SortableVerticalBannerItemProps> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { onDeleteItem, onChange, onChangeImage, item } = props;

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const files = e.target.files;
      onChangeImage && onChangeImage(files);
    }
  };

  return (
    <Card ref={setNodeRef} style={{ ...style, marginBottom: '16px' }} bodyStyle={{ padding: '16px' }} hoverable>
      <div style={{ display: 'flex', gap: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '400px' }}>
          <div style={{ marginBottom: '12px', position: 'relative' }}>
            <div
              style={{
                width: '100%',
                borderRadius: '12px',
                height: '110px',
                backgroundColor: 'white',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}>
              {item.imageUrlTemp || item.imageUrl ? (
                <img
                  style={{ width: '200px', height: '110px', objectFit: 'cover' }}
                  src={cdnImageUrl(item.imageUrlTemp || item.imageUrl)}
                  alt="event banner"
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#f0f0f0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#999',
                  }}>
                  NO IMAGE
                </div>
              )}
            </div>
          </div>
          <FileUploadButton
            accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
              .map((ext) => {
                return '.' + ext;
              })
              .join(',')}
            className="link-button"
            style={{
              fontSize: '14px',
              height: '32px',
              padding: '4px 15px',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              border: '1px solid #d9d9d9',
              color: 'rgba(0, 0, 0, 0.88)',
              boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
              cursor: 'pointer',
            }}
            onChange={handleImageChange}>
            이미지 변경
          </FileUploadButton>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <textarea
            value={item.title}
            onChange={(e) => {
              const lines = e.target.value.split('\n');
              if (lines.length <= 2) {
                onChange && onChange({ ...item, title: e.target.value });
              }
            }}
            placeholder="제목"
            style={inputStyle}
            rows={2}
          />
          <textarea
            value={item.subtitle}
            onChange={(e) => {
              const lines = e.target.value.split('\n');
              if (lines.length <= 2) {
                onChange && onChange({ ...item, subtitle: e.target.value });
              }
            }}
            placeholder="부제목"
            style={inputStyle}
            rows={2}
          />

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <div style={{ fontSize: '12px', color: '#666' }}>배경 색상</div>
              <ColorPicker
                value={item.backgroundColor || '#ffffff'}
                onChange={(color) => {
                  onChange && onChange({ ...item, backgroundColor: color.toHexString() });
                }}
                showText
                size="small"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <div style={{ fontSize: '12px', color: '#666' }}>부제목 색상</div>
              <ColorPicker
                value={item.subTitleColor || '#ffffff'}
                onChange={(color) => {
                  onChange && onChange({ ...item, subTitleColor: color.toHexString() });
                }}
                showText
                size="small"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>순서</div>
              <InputNumber
                value={item.order}
                onChange={(value) => {
                  onChange && onChange({ ...item, order: value || 0 });
                }}
                placeholder="순서(필수X)"
                min={0}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ border: '1px solid #eaeaea', borderRadius: '8px', padding: '8px' }}>
            <div>클릭시 이동 설정</div>
            <DeeplinkActionEdit
              data={item}
              onChange={(value) => {
                onChange && onChange({ ...item, ...value });
              }}></DeeplinkActionEdit>
          </div>
        </div>
        <Space direction="vertical" style={{ marginLeft: 'auto' }}>
          <Button icon={<DragOutlined />} {...attributes} {...listeners} style={{ cursor: 'grab' }} />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              onDeleteItem && onDeleteItem(e);
            }}
          />
        </Space>
      </div>
    </Card>
  );
};

export const CustomerBenefitEventsPage: React.FC = () => {
  const [editedFeedData, setEditedFeedData] = useState<FeedData<CustomerBenefitEventItem>>({
    type: 'customer-benefit-events',
  });
  const [modifiedFiles, setModifiedFiles] = useState<{ [key: string]: File }>({});

  const getCustomerBenefitEventsQuery = useQuery({
    queryKey: ['customerBenefitEvents'],
    queryFn: async () => {
      const response = await api.getCustomerBenefitEvents();
      return response;
    },
  });

  const updateCustomerBenefitEventMutation = useMutation({
    mutationFn: api.updateCustomerBenefitEvent,
    onSuccess: async () => {
      toast.dark('이벤트 모아보기 페이지가 저장되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
      });
    },
  });

  const data =
    getCustomerBenefitEventsQuery.data ||
    ({ type: 'customer-benefit-events', data: [] } as Feed<CustomerBenefitEventItem>);
  const contents = editedFeedData?.data || (data as FeedData<CustomerBenefitEventItem>).data || [];

  const sortableSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const onSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      if (contents) {
        const oldIndex = contents.findIndex(
          (item: CustomerBenefitEventItem, index: number) => `item-${index}-${item.imageUrl || ''}` === active.id,
        );
        const newIndex = contents.findIndex(
          (item: CustomerBenefitEventItem, index: number) => `item-${index}-${item.imageUrl || ''}` === over?.id,
        );
        const newData = arrayMove(contents, oldIndex, newIndex);
        setEditedFeedData((prev) => ({ ...prev, data: newData }));
      }
    }
  };

  const handleSave = async () => {
    try {
      const formData: FormData = new FormData();
      const json = { ...editedFeedData };

      // Remove temporary URLs before saving
      if (json.data) {
        json.data = json.data.map((item: CustomerBenefitEventItem) => {
          const { ...rest } = item;
          return rest;
        });
      }

      json.data = removeTempProperties(json.data);

      formData.append('json', JSON.stringify(json));
      Object.values(modifiedFiles).forEach((file) => {
        formData.append('files', file);
      });

      updateCustomerBenefitEventMutation.mutate(formData);
      message.success('저장되었습니다.');
    } catch (error) {
      message.error('저장 중 오류가 발생했습니다.');
    }
  };

  const onChangeImage = async (files: FileList, index: number) => {
    const { fileName } = extractFileName(files[0].name);
    const newFiles = await Promise.all(
      Array.from(files)
        .slice(0, 10)
        .map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 640, maxHeight: 880 });
          return result;
        }),
    );
    const contentTypes = newFiles[0].type.split('/');
    const uploadFileName = `${fileName}.${contentTypes[1]}`;
    const newImageFile = new File([newFiles[0].image], `${fileName}.${contentTypes[1]}`) as RcFile;
    const tempUrl = URL.createObjectURL(newFiles[0].image);
    setEditedFeedData((prev) => {
      const newValues = [...(prev?.data || contents)];
      newValues[index] = { ...newValues[index], imageUrl: uploadFileName, imageUrlTemp: tempUrl };
      return { ...prev, data: newValues };
    });

    setModifiedFiles((value) => {
      const newValue = { ...value };
      newValue[`${index}`] = newImageFile;
      return newValue;
    });
  };

  useEffect(() => {
    if (getCustomerBenefitEventsQuery.data) {
      setEditedFeedData(getCustomerBenefitEventsQuery.data);
    }
  }, [getCustomerBenefitEventsQuery.data]);

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div
        className="page-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div> 이벤트 모아보기 페이지 관리</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            icon={<PlusOutlined />}
            onClick={() => {
              setEditedFeedData((prev) => ({
                ...prev,
                data: [{} as CustomerBenefitEventItem, ...(prev?.data || contents || [])],
              }));
            }}>
            이벤트 추가
          </Button>
          <Button type="primary" onClick={handleSave}>
            저장
          </Button>
        </div>
      </div>
      <div className="page-content" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: '16px', flex: 1, overflow: 'hidden' }}>
          <div style={{ padding: '24px', width: '800px', height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '12px' }}>타이틀</div>
              <input
                style={inputStyle}
                value={editedFeedData?.title || ''}
                onChange={(e) => {
                  setEditedFeedData((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }));
                }}
                placeholder="타이틀을 입력하세요"
              />
            </div>
            <DndContext sensors={sortableSensors} collisionDetection={closestCenter} onDragEnd={onSortEnd}>
              <SortableContext
                items={contents.map(
                  (item: CustomerBenefitEventItem, index: number) => `item-${index}-${item.imageUrl || ''}`,
                )}
                strategy={verticalListSortingStrategy}>
                <div style={{ height: 'calc(100% - 91px)' }}>
                  {contents.map((item: CustomerBenefitEventItem, index: number) => {
                    return (
                      <SortableVerticalBannerItem
                        key={`item-${index}-${item.imageUrl || ''}`}
                        item={item}
                        id={`item-${index}-${item.imageUrl || ''}`}
                        onDeleteItem={() => {
                          setEditedFeedData((prev) => {
                            const newValues = [...(prev?.data || contents)];
                            newValues.splice(index, 1);
                            return { ...prev, data: newValues };
                          });
                        }}
                        onChangeImage={(files: FileList) => {
                          onChangeImage(files, index);
                        }}
                        onChange={(item: CustomerBenefitEventItem) => {
                          setEditedFeedData((prev) => {
                            const newValues = [...(prev?.data || contents)];
                            newValues[index] = item;
                            return { ...prev, data: newValues };
                          });
                        }}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
          <CustomerBenefitEventsPreview data={editedFeedData} />
        </div>
      </div>
    </div>
  );
};
